/* eslint-disable no-param-reassign */

import { constants } from '../../config/constants';
import {
  sponsoredClickService,
  sponsoredImpressionService,
  sponsoredVisitService,
} from '../../services/sponsoredService';
import { CookieStorage } from '../../utils/CookieStorage';
import { isWindow } from '../../utils/deviceChecker';
import { sponsoredEvents } from '../../utils/SponsoredHelper';

const { COUNTRIES_ABBREVIATIONS: REGION_CODE } = constants;

const setSponsoredDisplayDigitalDataImpression = (mPos, sku) => {
  const SPONSORED_DISPLAY_POSITION = {
    9: 'bottom',
    10: 'side',
  };
  const eventToDigitalData = new CustomEvent('DDXLPSponsoredDisplayImpression', {
    bubbles: true,
    detail: {
      payload: `${sku}|${SPONSORED_DISPLAY_POSITION[mPos]}`,
    },
  });
  window.dispatchEvent(eventToDigitalData);
};

const setSponsoredDisplayDigitalDataClick = (mPos, sku) => {
  const SPONSORED_DISPLAY_POSITION = {
    9: 'bottom',
    10: 'side',
  };
  const eventToDigitalData = new CustomEvent('DDXLPSponsoredDisplayClick', {
    bubbles: true,
    detail: {
      payload: `${sku}|${SPONSORED_DISPLAY_POSITION[mPos]}`,
    },
  });
  window.dispatchEvent(eventToDigitalData);
};
const getFuntionAnalyticsSponsored = (isSponsoredService) => {
  if (isSponsoredService)
    return {
      sponsoredClickService,
      sponsoredImpressionService,
      sponsoredVisitService,
    };

  return {
    sponsoredClickService: sponsoredEvents.click,
    sponsoredImpressionService: sponsoredEvents.impression,
    sponsoredVisitService: sponsoredEvents.visit,
  };
};

const getPageId = (pageName) => {
  const pagesIds = {
    category: 'categoryId',
    search: 'Ntt',
  };

  return pagesIds[pageName] || '';
};

const getIsSponsoredDisplayEnabled = (appCtx, Ntt, id) => {
  const { siteConfig, store = 'falabella' } = appCtx;
  const topSortConfig = siteConfig?.configurations?.topSortExperimentConfig ?? {};
  const featureEnableSponsoredDisplay = siteConfig?.toggles?.featureEnableSponsoredDisplay ?? false;

  const disabledStoresSponsoredProductsJson = siteConfig?.configurations?.disabledStoresSponsoredProducts ?? {};

  const disabledStoresSponsoredProducts = disabledStoresSponsoredProductsJson?.stores ?? [];
  const categoryConfig = new Set(topSortConfig?.categories || []);
  const searchTermConfig = new Set(topSortConfig?.searchTerms || []);
  const isSearchTermInExperiment = searchTermConfig?.has(Ntt);
  const isCategoryInExperiment = categoryConfig?.has(id);
  const hideSD = isCategoryInExperiment || isSearchTermInExperiment;

  const isSponsoredDisplayEnabled =
    featureEnableSponsoredDisplay && !disabledStoresSponsoredProducts.includes(store) && !hideSD;

  return isSponsoredDisplayEnabled;
};
const getAdInjectorEnabled = (appCtx) => {
  const { siteConfig } = appCtx;
  const currentStore = appCtx.store ?? 'falabella';
  const enabledStores = siteConfig?.configurations?.adInjectorStoresEnabled ?? [];
  if (enabledStores.includes(currentStore)) {
    return siteConfig?.toggles?.adInjectorIsEnabled ?? false;
  }
  return false;
};
const getUrl = (url, store) => {
  const storesAvaliable = ['tottus', 'linio', 'sodimac', 'homecenter'];

  if (storesAvaliable.includes(store)) return `${url}?exp=${store}`;

  return url;
};

const getSponsorBrandCookie = (appCtx) => {
  const sbExperimentPositionName = constants.SB_EXPERIMENT_POSITION;
  const cookies = CookieStorage.parseCookies(appCtx.ssrCookies);
  const sbExperimentPosition = cookies?.[sbExperimentPositionName];

  if (!sbExperimentPosition) {
    return undefined;
  }

  try {
    return JSON.parse(sbExperimentPosition);
  } catch {
    return undefined;
  }
};

function getShouldShowSponsoredBrand({ appCtx, pageName, name, deviceType, index }) {
  const isSponsorBrandEnabled = appCtx?.siteConfig?.toggles?.isSponsorBrandEnabled ?? false;
  if (!isSponsorBrandEnabled) return false;

  const sponsorBrandPosition =
    getSponsorBrandCookie(appCtx) || (appCtx?.siteConfig?.configurations?.vasConfig?.sponsorBrandPosition ?? {});

  const desktopItemsPerRow = 4;
  const mobileItemsPerRow = 2;

  const defaultSponsoredBrandPosition =
    Object.keys(sponsorBrandPosition?.default || {}).length === 0
      ? constants.DEFAULT_SPONSORED_BRAND_POSITION
      : sponsorBrandPosition.default;

  const isSearchPage = pageName === 'search';
  const positionPerDeviceType = isSearchPage
    ? sponsorBrandPosition.search || defaultSponsoredBrandPosition
    : sponsorBrandPosition[name] || defaultSponsoredBrandPosition;

  const indexForSB = positionPerDeviceType[deviceType];
  const shouldShowSponsoredBrand = index === indexForSB;
  const itemsPerRow = deviceType === 'desktop' ? desktopItemsPerRow : mobileItemsPerRow;
  const rowNumber = Math.ceil((indexForSB + 1) / itemsPerRow);

  return { shouldShowSponsoredBrand, rowNumber };
}

export const dispatchDigitalData = (eventName, payload) => {
  const event = new CustomEvent(eventName, {
    bubbles: true,
    detail: { payload },
  });
  window.dispatchEvent(event);
};

export const sendDigitalDataForRender = (brandName, position) => {
  const payload = `${brandName}|${position}`;

  dispatchDigitalData('DDXLPSponsoredBrandRender', payload);
};

export const sendDigitalDataForImpression = (brandName, position, totalProducts) => {
  const payload = `${brandName}|${position}|${totalProducts}`;

  dispatchDigitalData('DDXLPSponsoredBrandImpression', payload);
};

export const sendDigitalDataForClick = (brandName, position, totalProducts, skuClicked) => {
  const elementType = skuClicked ? 'product' : 'custom image';
  const skuOrImage = skuClicked || 'image';
  const payload = `${brandName}|${position}|${totalProducts}|${elementType}|${skuOrImage}`;

  dispatchDigitalData('DDXLPSponsoredBrandClick', payload);
};

export const sendDigitalDataSponsoredProductClick = (position, sku, isCallFromAddToCart = false, isClick = false) => {
  if (!isClick) return;
  const payload = `[${sku}]|[${position}]`;
  const eventName = isCallFromAddToCart ? 'DDXLPSponsoredProductClickCart' : 'DDXLPSponsoredProductClick';
  dispatchDigitalData(eventName, payload);
};

const handleFetchComplete = (rowNumber, brandNameRef, SBproductLength) => (data) => {
  if (data?.sponsoredProducts?.length) {
    const brandName = data.sponsoredProducts[0]?.brand || '';
    const productLength = Math.min(data.sponsoredProducts.length, 3);
    brandNameRef.current = brandName;
    SBproductLength.current = productLength;
    sendDigitalDataForRender(brandName, rowNumber);
  }
};

const getHandleBannerActionForSponsorDisplay = ({
  appCtx,
  config,
  categoryName,
  categoryId,
  mPos,
  hasImpressionBeenSent,
  setHasImpressionBeenSent,
}) => {
  const { tenant, deviceType, regionCode, store } = appCtx;

  const { MABAYA_TOKEN, MABAYA_EVENTS_ENABLED, FEATURE_TOGGLE_SPONSORED_VAS_SERVICE } = config;

  const handleBannerAction = (product, action, adType) => {
    const providerName = product?.providerName ?? null;
    /* istanbul ignore next */
    if (product != null) {
      const sku = product.id;
      const { adInfo } = product;
      const params = {
        mabayaToken: MABAYA_TOKEN,
        enabled: MABAYA_EVENTS_ENABLED,
        platform: deviceType,
        referrerUrl: window.location.href,
        sku,
        regionCode,
        adInfo,
        mPos,
        categoryName,
        categoryId,
        tenant,
        store,
        ...(providerName ? { providerName } : {}),
        adType,
        productId: product.productId,
      };
      const isSponsoredService = FEATURE_TOGGLE_SPONSORED_VAS_SERVICE || false;
      const {
        sponsoredClickService: clickService,
        sponsoredImpressionService: impressionService,
        sponsoredVisitService: visitService,
      } = getFuntionAnalyticsSponsored(isSponsoredService);

      if (action === 'click') {
        clickService(params);
        setSponsoredDisplayDigitalDataClick(mPos, sku);
      }
      if (action === 'impression' && !hasImpressionBeenSent) {
        impressionService(params);
        setSponsoredDisplayDigitalDataImpression(mPos, sku);
        setHasImpressionBeenSent(true);
      }

      if (action === 'visit') {
        visitService(params);
      }
    }
  };

  return handleBannerAction;
};

const getHandleBrandActionForSponsoredBrand = ({
  appCtx,
  config,
  categoryName,
  hasImpressionBeenSent,
  setHasImpressionBeenSent,
  pageName,
  brandNameRef,
  SBproductLength,
  router,
}) => {
  const { tenant, deviceType, regionCode, store } = appCtx;
  const { MABAYA_TOKEN, MABAYA_EVENTS_ENABLED, FEATURE_TOGGLE_SPONSORED_VAS_SERVICE } = config;
  const { BRANDS } = constants.AD_TYPES_FOR_VAS;
  const name = router?.query?.name || '';
  const { rowNumber } = getShouldShowSponsoredBrand({
    appCtx,
    pageName,
    categoryName,
    deviceType,
    name,
  });
  const handleBrandAction = (product, eventType, mabayaAdInfo) => {
    const mabayaAdInfoString = mabayaAdInfo.join(',');
    const params = {
      mabayaToken: MABAYA_TOKEN,
      adType: BRANDS,
      enabled: MABAYA_EVENTS_ENABLED,
      platform: deviceType,
      referrerUrl: window.location.href,
      regionCode,
      adInfo: mabayaAdInfoString,
      categoryName,
      tenant,
      store,
    };

    const isSponsoredService = FEATURE_TOGGLE_SPONSORED_VAS_SERVICE || false;

    const { sponsoredClickService: clickService, sponsoredImpressionService: impressionService } =
      getFuntionAnalyticsSponsored(isSponsoredService);

    if (eventType === 'click') {
      clickService(params);
      const sku = product?.id;
      sendDigitalDataForClick(brandNameRef.current, rowNumber, SBproductLength.current, sku);
    }

    if (eventType === 'impression' && !hasImpressionBeenSent) {
      sendDigitalDataForImpression(brandNameRef.current, SBproductLength.current, rowNumber);
      impressionService(params);
      setHasImpressionBeenSent(true);
    }
  };

  return handleBrandAction;
};

const getBaseDomain = (url) => {
  if (typeof url !== 'string') {
    return null;
  }
  const urlObj = new URL(url);
  return urlObj.origin;
};

const getUrlBase = (config) => {
  const urlBase = isWindow() ? config.API_HOST : config.CLUSTER_API_HOST;

  return getBaseDomain(urlBase);
};

let cachedSearch = '';
let cachedFacetParams = {};

const getFacetParams = () => {
  if (typeof window === 'undefined') return {};
  const currentSearch = window.location.search;
  if (currentSearch === cachedSearch) {
    return cachedFacetParams;
  }
  cachedSearch = currentSearch;
  const urlParams = new URLSearchParams(currentSearch);
  cachedFacetParams = {};
  urlParams.forEach((value, key) => {
    cachedFacetParams[key] = value;
  });
  return cachedFacetParams;
};

const getMaxSponsorBrandCookie = (appCtx) => {
  const sbExperimentMaxName = constants.SB_EXPERIMENT_MAX_ELEMENTS;
  const cookies = CookieStorage.parseCookies(appCtx.ssrCookies);
  const sbExperimentMax = cookies?.[sbExperimentMaxName];

  if (!sbExperimentMax) {
    return undefined;
  }
  return Number(sbExperimentMax);
};

const getPathName = (url) => {
  if (typeof url !== 'string') {
    return null;
  }
  const { pathname } = new URL(url);
  return pathname;
};

const getHostUrl = ({ config, store, regionCode }) => {
  if (!isWindow()) {
    return config.CLUSTER_API_HOST;
  }
  const storeMapping = {
    so_com: [REGION_CODE.CHILE, REGION_CODE.PERU],
    sodimac: [REGION_CODE.CHILE, REGION_CODE.PERU],
  };
  const regionsWithRelativePath = storeMapping[store] ?? [];
  const withRelativePath = regionsWithRelativePath.includes(regionCode);
  const canUsePathName = withRelativePath && config.NODE_ENV === 'production';
  if (!canUsePathName) {
    return config.API_HOST;
  }
  return getPathName(config.API_HOST);
};

const getInstallmentData = (installmentsWithoutInterest = []) => {
  const uniqueInstallments = [...new Set(installmentsWithoutInterest.map((item) => Number(item.installments)))];

  const maxInstallments = Math.max(...uniqueInstallments);

  return maxInstallments > 0 ? `${maxInstallments} ${constants.INSTALLMENT_WITHOUT_INTEREST_TEXT}` : '';
};

export {
  getAdInjectorEnabled,
  getFacetParams,
  getFuntionAnalyticsSponsored,
  getHandleBannerActionForSponsorDisplay,
  getHandleBrandActionForSponsoredBrand,
  getHostUrl,
  getInstallmentData,
  getIsSponsoredDisplayEnabled,
  getMaxSponsorBrandCookie,
  getPageId,
  getShouldShowSponsoredBrand,
  getUrl,
  getUrlBase,
  handleFetchComplete,
};
